// Copyright (c) StrangeBee 2023

import {
    ExportController,
    ExportEntitiesType,
    ExportInput,
    isRequired,
    PossibleNamedQuery,
    QueryParams,
    QuerySelector,
} from 'thehive-sdk';

import { ExportValuesForm } from '@Organisms/Export/ExportDrawer/ExportForm';
import client from '@Client';
import moment from 'moment';

interface OnExport {
    onExport: (options: ExportValuesForm, objectIds?: string[], queryParams?: QueryParams) => void;
}

export type ExportEventList = Record<ExportEntitiesType, OnExport>;

interface ExportEventListParams {
    locale: (label: string) => string;
    controller: ExportController;
    onSuccess?: (count?: boolean, message?: string) => void;
    onError?: (label?: string) => void;
    queryParams?: QueryParams;
}

const queryHeadNameMap: Record<ExportEntitiesType, PossibleNamedQuery> = {
    Case: 'listCase',
    Alert: 'listAlert',
    User: 'listUser',
    Organisation: 'listOrganisation',
    Observable: 'listObservable',
    Procedure: 'listProcedure',
    Task: 'listTask',
};

const getPayload = (
    typeEntity: ExportEntitiesType,
    options: ExportValuesForm,
    objectIds?: string[],
    listQueryParams?: QueryParams,
): ExportInput | false => {
    const queryHeadName = queryHeadNameMap[typeEntity];
    if (!queryHeadName) {
        return false;
    }
    const querySelector: QuerySelector = {
        head:
            listQueryParams && listQueryParams.selector && listQueryParams.selector.head
                ? listQueryParams.selector.head
                : [{ name: queryHeadName }],
        name: 'export-data',
    };

    // If bulk edit
    if (objectIds && objectIds.length) {
        querySelector.initialFilters = [
            {
                field: '_id',
                operator: 'in',
                values: objectIds,
                type: 'string',
            },
        ];
    }

    // Pagination is a requirement of API, ListQueryParams can be undefined
    const queryParams: QueryParams = {
        ...listQueryParams,
        selector: {
            ...(listQueryParams?.selector ?? {}),
            ...querySelector,
        },
        paginationOptions: {
            page: 0,
            itemsPerPage: objectIds && objectIds.length ? objectIds.length : 999999,
        },
    };
    const query = client.getBuilder().prepare({
        ...queryParams,
    });
    return {
        ...query,
        options: {
            fileName: `${typeEntity}-export-${moment().format('YYYYMMDD-HH:mm')}`,
            format: options.format,
            model: typeEntity,
            delimiter: options.delimiter === '\\t' ? '\t' : options.delimiter,
        },
    };
};

const callController = (
    typeEntity: ExportEntitiesType,
    controller: ExportController,
    onError: ((label?: string) => void) | undefined,
    options: ExportValuesForm,
    objectIds?: string[],
    listQueryParams?: QueryParams,
): void => {
    if (!listQueryParams) {
        isRequired(objectIds);
    } else {
        isRequired(listQueryParams);
    }

    const payload = getPayload(typeEntity, options, objectIds, listQueryParams);

    if (false === payload) {
        return onError ? onError('Bad arguments') : undefined;
    }

    const url = window.location.origin + client.apiUrl + controller.getUrlExport(typeEntity, payload);
    window.open(url, '_blank');
};

const createExportEvent = (
    eventName: ExportEntitiesType,
    controller: ExportController,
    onError?: (label?: string) => void,
): OnExport => ({
    onExport: (options: ExportValuesForm, objectIds?: string[], queryParams?: QueryParams) => {
        callController(eventName, controller, onError, options, objectIds, queryParams);
    },
});

export const computeEventList = ({ controller, onError }: ExportEventListParams): ExportEventList => ({
    Case: createExportEvent('Case', controller, onError),
    Alert: createExportEvent('Alert', controller, onError),
    User: createExportEvent('User', controller, onError),
    Organisation: createExportEvent('Organisation', controller, onError),
    Observable: createExportEvent('Observable', controller, onError),
    Procedure: createExportEvent('Procedure', controller, onError),
    Task: createExportEvent('Task', controller, onError),
});
